import { useEffect, useState } from 'react';
import { api, type Mes, type Yo } from '../services/api';
import { hoy, mesDe, sumarMeses } from '../nucleo/fechas';
import { PlacaMes } from './PlacaMes';

/** Cuántos días del mes nuevo sigue el cartel del mes que cerró. */
export const DIAS_CARTEL_MES = 7;

const CLAVE_CERRADO = 'inicio.cartelMesCerrado';

function leerCerrado(): string | null {
  try {
    return localStorage.getItem(CLAVE_CERRADO);
  } catch {
    return null;
  }
}

/**
 * "Septiembre cerró": el cartel de Inicio de los primeros días del mes.
 *
 * Sale solo con el mes anterior CERRADO (el 1 a las 6:00): antes el campeón
 * todavía puede cambiar. Dice quién ganó y cómo te fue a vos, y lleva al
 * resumen del mes ("tu mes") y a la placa, que es lo que se manda al grupo.
 * Se puede cerrar; se acuerda en este celular y vuelve el mes que viene.
 */
export function CartelMes({ yo, onVerResumen }: { yo: Yo; onVerResumen: (mes: string) => void }) {
  const hoyEs = hoy();
  const mes = sumarMeses(mesDe(hoyEs), -1);
  const enFecha = Number(hoyEs.slice(8)) <= DIAS_CARTEL_MES;
  const [datos, setDatos] = useState<Mes | null>(null);
  const [cerrado, setCerrado] = useState(() => leerCerrado() === mes);
  const [viendoPlaca, setViendoPlaca] = useState(false);

  useEffect(() => {
    if (!enFecha || cerrado) return;
    let vivo = true;
    api
      .mes(mes)
      .then((d) => vivo && setDatos(d))
      // Es un cartel de más: si falla, no se muestra y listo.
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [mes, enFecha, cerrado]);

  if (!enFecha || cerrado || !datos || !datos.cerrado || datos.tabla.length === 0) return null;

  function cerrar() {
    try {
      localStorage.setItem(CLAVE_CERRADO, mes);
    } catch {
      // Sin almacenamiento se cierra igual; vuelve a salir la próxima vez.
    }
    setCerrado(true);
  }

  const campeones = datos.reconocimientos.campeones;
  const soyCampeon = campeones.some((p) => p.id === yo.jugador.id);
  const mia = datos.tabla.find((f) => f.id === yo.jugador.id);
  const nombreMes = datos.titulo.split(' ')[0]!;

  return (
    <section className="flex flex-col gap-2">
      <div className="relative rounded-2xl border-2 border-(--color-oro) bg-(--color-tarjeta) px-4 py-3">
        <button
          onClick={cerrar}
          aria-label="Cerrar el cartel"
          className="absolute right-1 top-1 min-h-10 min-w-10 text-xl text-(--color-apagado)"
        >
          ×
        </button>
        <p className="font-titulo text-3xl uppercase text-(--color-oro)">{nombreMes} cerró</p>
        {campeones.length > 0 && (
          <p className="text-lg font-semibold">
            {soyCampeon
              ? '👑 ¡Sos el campeón del mes!'
              : `👑 ${campeones.length > 1 ? 'Campeones' : 'Campeón'}: ${campeones.map((p) => p.nombre).join(' y ')}`}
          </p>
        )}
        {mia && !soyCampeon && (
          <p className="text-sm text-(--color-apagado)">
            {mia.elegible
              ? `Vos terminaste ${mia.posicion}º con ${mia.puntos} puntos.`
              : `Jugaste ${mia.partidos} ${mia.partidos === 1 ? 'partido' : 'partidos'}: no alcanzó para entrar en la corona.`}
          </p>
        )}
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => onVerResumen(mes)}
            className="flex-1 rounded-xl bg-(--color-acento) py-2.5 text-center font-semibold
              text-(--color-sobre-acento) transition active:scale-[0.98]"
          >
            📋 Ver tu mes
          </button>
          {!viendoPlaca && (
            <button
              onClick={() => setViendoPlaca(true)}
              className="flex-1 rounded-xl border border-(--color-acento) bg-(--color-panel) py-2.5
                text-center font-semibold transition active:scale-[0.98]"
            >
              📸 Placa
            </button>
          )}
        </div>
      </div>
      {viendoPlaca && <PlacaMes mes={mes} titulo={datos.titulo} onCerrar={() => setViendoPlaca(false)} />}
    </section>
  );
}
